import { useState, useEffect, useRef, useMemo } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useParams } from 'react-router-dom';
import { Row, Col, Card, Button, Label } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import Select from 'react-select';
import { FaRegStar, FaStar, FaFilter } from 'react-icons/fa';
import { IoMdInformationCircle } from 'react-icons/io';
import { Tooltip } from '@mui/material';
import { AutoSizer, Column, Table } from 'react-virtualized';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme, selectStyles, usStatesAndTerritoriesOptions } from '../../../config/constants';
import reportingApi from '../../../api/reportingApi';
import LocalStorage from '../../../sharedComponents/LocalStorage/LocalStorage';
import formatters from '../../../config/formatters';
import { Pagination } from '@mantine/core';
import useReports from '../useReports';
import { showModal } from '../../../reducers/ModalsReducer/ModalsActions';
import ShareReportModal from '../Modals/ShareReportModal';
import SaveAsCopyModal from '../Modals/SaveAsCopyModal';
import { FaArrowLeft, FaTrash, FaRegEdit } from 'react-icons/fa';
import moment from 'moment';
import DeleteReportModal from '../Modals/DeleteReportModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsClipboardDataFill } from 'react-icons/bs';
import { getLastCompletedQuarterDates, getTrailing13MonthsDates } from '../../../utils/dateUtils';
import PageHeader from '../../../sharedComponents/Header/PageHeader';
import DateRangePicker from '../../../sharedComponents/DateRangePicker/DateRangePicker';
import SelectAllSearch from '../../../sharedComponents/Select/SelectAllSearch';
import ManufacturerSelect from "../../../sharedComponents/ManufacturerSelect/ManufacturerSelect";

const ReportWidget = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { exportReport,
        isExporting,
        useReportsList,
        useReportsFilters,
        useReportContent,
        portalTypeReportsList,
        recentReportsList,
        userReportsList,
        favoritedReportsList,
        selectedReportPage,
        setSelectedReportPage,
        getSelectedReportMetadata } = useReports();

    // React Query Mutations
    const updateReportMutation = useMutation({
        mutationFn: ({ id, params }) => reportingApi.updateReport.patch(id, params),
        onSuccess:async (response) => {
            if (response.data) {
                setSelectedReportMetadata(response.data);
                queryClient.invalidateQueries((['reportsList', 'reportContent']));
                queryClient.removeQueries({ queryKey: 'reportContent' });
            }
        },
        onError: (error) => {
            console.error('Error updating report:', error);
        }
    });

    const updateReportNameMutation = useMutation({
        mutationFn:async ({ id, name }) => await reportingApi.updateReport.patch(id, { name }),
        onSuccess: (response, variables) => {
            setSelectedReportMetadata(response.data);
            queryClient.invalidateQueries((['reportsList']));
            setIsEditingName(false);
        },
        onError: (error) => {
            console.error('Error updating report name:', error);
            setIsEditingName(false);
        }
    });

    const updateReportFavoriteMutation = useMutation({
        mutationFn: ({ id, favourite }) => reportingApi.updateReport.patch(id, { favourite }),
        onSuccess: (response, variables) => {
            setIsStarred(variables.favourite);
        },
        onError: (error) => {
            console.error('Error updating report favorite status:', error);
        }
    });
    const { reportId } = useParams();
    const { data: reportsList, isFetched: reportsListFetched } = useReportsList();
    const selectedManufacturers = useSelector(({user}) => user.selectedManufacturers);
    const { data: selectedReportContents, isLoading: reportLoading } = useReportContent(reportId, selectedReportPage, selectedManufacturers);
    const { data: reportFilters, isPending: filterLoading } = useReportsFilters();
    const [selectedReportMetadata, setSelectedReportMetadata] = useState(getSelectedReportMetadata(reportId, reportsList));
    const [columnKeys, setColumnKeys] = useState([]);
    const mfrId = LocalStorage.manufacturerData.get().platform_id;
    const [startDate, endDate] = getTrailing13MonthsDates();
    const [isStarred, setIsStarred] = useState(selectedReportMetadata?.favourite || false);
    const [selectedDateRange, setSelectedDateRange] = useState({startDate: startDate, endDate: endDate});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [selectedStates, setSelectedStates] = useState(null);
    const [isReportHovered, setIsReportHovered] = useState(false);
    const [childFilters, setChildFilters] = useState([]);
    const [selectedChildren, setSelectedChildren] = useState(null);
    const [childLoading, setChildLoading] = useState(false);
    const [reportSelectValue, setReportSelectValue] = useState(null);
    const { numberFormatter, percentFormatter, usCurrencyFormatter } = formatters();
    const tableRef = useRef(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const inputRef = useRef(null);
    const [sortField, setSortField] = useState(selectedReportMetadata?.sortField ?? null);
    const [sortType, setSortType] = useState(selectedReportMetadata?.sortType ?? null);
    const currentParentPage = useMemo(() => {
        const locationPath = location.pathname;
        if (locationPath.includes('allreports')) {
            return 'allreports';
        } else if (locationPath.includes('recentreports')) {
            return 'recentreports'
        } else if (locationPath.includes('userreports')) {
            return 'userreports';
        } else if (locationPath.includes('favoritereports')) {
            return'favoritereports';
        } else {
            return 'allreports';
        }
    }, [location]);

    const reportSelectOptions = useMemo(() => {
        let reportsListOptions = [];
        switch(currentParentPage) {
        case ('allreports') :
            reportsListOptions = portalTypeReportsList;
            break;
        case ('recentreports') :
            reportsListOptions = recentReportsList;
            break;
        case ('userreports') :
            reportsListOptions = userReportsList;
            break;
        case ('favoritereports') :
            reportsListOptions = favoritedReportsList;
            break;
        default :
            break;
        }
        return reportsListOptions?.map((report) => ({
            label: report.name,
            value: report.id,
        }));
        // eslint-disable-next-line
    }, [reportsListFetched]);

    const handleSelectChange = (report) => {
        const selectedReportOption = reportSelectOptions?.find((reportOption) => reportOption.value === report.id);
        setReportSelectValue(selectedReportOption);
        queryClient.removeQueries((['reportContent']));
        setSelectedReportMetadata(getSelectedReportMetadata(report.value, reportsList));
        navigate('/' + currentParentPage + '/details/' + report.value);
    };

    const handleReportChange = (report) => {
        const selectedReportMetadata = reportSelectOptions?.find((reportOption) => reportOption.value === report.id);
        setReportSelectValue(selectedReportMetadata);
        navigate('/' + currentParentPage + '/details/' + report.id);
    };

    const handleShareClick = () => {
        const modalParams = {
            reportId: selectedReportMetadata?.id
        }
        dispatch(showModal(ShareReportModal, modalParams));
    };

    const handleStarClick = () => {
        updateReportFavoriteMutation.mutate({
            id: selectedReportMetadata?.id,
            favourite: !isStarred
        });
    };

    const handleCopyReport = () => {
        let selectedDates;
        if (selectedReportMetadata?.reportType?.name === 'Opportunity Monthly View Report') {
            selectedDates = getLastCompletedQuarterDates();
        } else {
            selectedDates = getTrailing13MonthsDates();
        }

        const modalParams = {
            parentPage: currentParentPage,
            reportTypeId: selectedReportMetadata?.reportType?.id,
            selectedDates,
            selectedProducts: selectedProduct?.map((product) => product?.value || '') || [],
            selectedCategories: selectedCategory?.map((category) => category?.value || '') || [],
            selectedOperators: selectedOperator?.map((operator) => operator?.value || '') || [],
            selectedSegments: selectedSegment?.map((segment) => segment?.value || '') || [],
            selectedChildren: selectedChildren?.map((child) => child?.value || '') || []
        }
        dispatch(showModal(SaveAsCopyModal, modalParams));
    };

    const handleDeleteReport = () => {
        const modalParams = {
            reportId: selectedReportMetadata?.id,
            parentPage: currentParentPage
        }
        dispatch(showModal(DeleteReportModal, modalParams));
    };

    const fetchChildrenForParents = async (parents) => {
        if (!parents || parents.length === 0) {
            setChildFilters([]);
            setChildLoading(false);
            return [];
        }
        setChildLoading(true);
        const payload = {
            manufacturerPlatformId: mfrId,
            portal: selectedReportMetadata?.reportType?.portal,
            parents: parents.map(p => p.value)
        };
        try {
            const res = await reportingApi.getChildLocations.post(payload);
            const options = (res.data || []).sort().map(name => ({
                value: name,
                label: name
            }));
            setChildFilters(options);
            return options;
        } catch (e) {
            console.error("Error fetching children:", e);
            setChildFilters([]);
            return [];
        } finally {
            setChildLoading(false);
        }
    };

    const patchReport = (categories, operators, products, segments, states, newSortField, newSortType, children) => {
        const categoriesToUse = categories ? categories : selectedCategory;
        const operatorsToUse = operators ? operators : selectedOperator;
        const productsToUse = products ? products : selectedProduct;
        const segmentsToUse = segments ? segments : selectedSegment;
        const statesToUse = states ? states : selectedStates;
        const childrenToUse   = children ?? selectedChildren;
        const params = {
            name: selectedReportMetadata.name,
            favourite: selectedReportMetadata.favourite,
            reportTypeId: selectedReportMetadata.reportType.reportTypeId,
            filters: {
                dateRange: [
                    moment(selectedDateRange.startDate).startOf('month').format('YYYY-MM-DD'),
                    moment(selectedDateRange.endDate).format('YYYY-MM-DD')
                ],
                categories: categoriesToUse?.map((category) => category?.value || '') || [],
                parentGroups: operatorsToUse?.map((operator) => operator?.value || '') || [],
                products: productsToUse?.map((product) => product?.value || '') || [],
                marketSegments: segmentsToUse?.map((segment) => segment?.value || '') || [],
                states: statesToUse?.map((state) => state?.value || '') || [],
                children:       childrenToUse?.map(ch => ch?.value || '') || [],
            },
            sortField: newSortField ?? sortField ?? selectedReportMetadata.sortField ?? 'total_volume',
            sortType: newSortType ?? sortType ?? selectedReportMetadata.sortType ?? 'desc'
        };
        updateReportMutation.mutate({
            id: selectedReportMetadata.id,
            params
        }, {
            onSuccess: () => {
                setSortField(params.sortField);
                setSortType(params.sortType);
            }
        });
    };

    const handleColumnHeaderClick = (dataKey) => {
        let key = formatters().camelCaseToUnderscore(dataKey);
        let newSortType = 'desc';
        if (sortField === key) {
            newSortType = sortType === 'desc' ? 'asc' : 'desc';
        }
        patchReport(null, null, null, null, null, key, newSortType);
    };

    const widgetColumns = columnKeys?.map((key, index) => {
            let isSorted = sortField === formatters().camelCaseToUnderscore(key);
            const sortIndicator = isSorted ? (sortType === 'asc' ? ' ▲' : ' ▼') : '';
            const headerRenderer = ({ label, dataKey }) => (
                <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleColumnHeaderClick(dataKey)}
                >
                    {label}{sortIndicator}
                </span>
            );
            if (key === 'percentageOfSales') {
                return (
                    <Column
                        key={index}
                        label={formatters().camelToTitleCase(key)}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => <span>{percentFormatter.format(cellData / 100)}</span>}
                        headerRenderer={headerRenderer}
                    />
                )
            } else if (key === 'totalVolume') {
                return (
                    <Column
                        key={index}
                        label={formatters().camelToTitleCase(key)}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => <span>{usCurrencyFormatter.format(cellData)}</span>}
                        headerRenderer={headerRenderer}
                    />
                )
            } else if (key === 'customerGroupId') {
                return (
                    <Column
                        key={index}
                        label={formatters().camelToTitleCase(key)}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => <span>{cellData}</span>}
                    />
                )
            }else if (key === 'manufacturerItemNumber') {
                if (!selectedReportMetadata?.ownerId) {
                    return (
                        <Column
                            key={index}
                            label={'MIN'}
                            dataKey={key}
                            width={tableRef?.current?.props?.width / columnKeys?.length}
                            cellRenderer={({ cellData }) => <span>{cellData}</span>}
                        />
                    )
                } else {
                    return (
                        <Column
                            key={index}
                            label={'MIN'}
                            dataKey={key}
                            width={tableRef?.current?.props?.width / columnKeys?.length}
                            cellRenderer={({ cellData }) => <span>{cellData}</span>}
                            headerRenderer={headerRenderer}
                        />
                    )
                }
            } else {
                if (!selectedReportMetadata?.ownerId) {
                    return (
                        <Column
                            key={index}
                            label={formatters().camelToTitleCase(key)}
                            dataKey={key}
                            cellRenderer={({ cellData }) => isNaN(cellData) ? <span>{cellData}</span> : <span>{numberFormatter.format(Math.round(cellData))}</span>}
                            width={tableRef?.current?.props?.width / columnKeys?.length}
                        />
                    )
                } else {
                    return (
                        <Column
                            key={index}
                            label={formatters().camelToTitleCase(key)}
                            dataKey={key}
                            width={tableRef?.current?.props?.width / columnKeys?.length}
                            cellRenderer={({ cellData }) => isNaN(cellData) ? <span>{cellData}</span> : <span>{numberFormatter.format(Math.round(cellData))}</span>}
                            headerRenderer={headerRenderer}
                        />
                    )
                }
            }
        });

    const setDefaultFilters = () => {
        let defaultCategory = [];
        let defaultOperator = [];
        let defaultProduct = [];
        let defaultSegment = [];
        let defaultStates = [];
        let defaultChildren = [];
        if (categoriesFilters?.length > 0) {
            selectedReportMetadata?.filters?.categories?.forEach((category) => {
                const foundCategory = categoriesFilters.find((cat) => cat.value === category);
                if (foundCategory) {
                    defaultCategory.push(foundCategory);
                }
            });
            setSelectedCategory(defaultCategory);
        }
        if (operatorsFilters?.length > 0) {
            selectedReportMetadata?.filters?.parentGroups?.forEach((operator) => {
                const foundOperator = operatorsFilters.find((op) => op.value === operator);
                if (foundOperator) {
                    defaultOperator.push(foundOperator);
                }
            });
            setSelectedOperator(defaultOperator);
        }
        if (productFilters?.length > 0) {
            selectedReportMetadata?.filters?.products?.forEach((product) => {
                const foundProduct = productFilters.find((prod) => prod.value === product);
                if (foundProduct) {
                    defaultProduct.push(foundProduct);
                }
            });
            setSelectedProduct(defaultProduct);
        }
        if (segmentsFilters?.length > 0) {
            selectedReportMetadata?.filters?.marketSegments?.forEach((segment) => {
                const foundSegment = segmentsFilters.find((seg) => seg.value === segment);
                if (foundSegment) {
                    defaultSegment.push(foundSegment);
                }
            });
            setSelectedSegment(defaultSegment);
        }
        if (usStatesAndTerritoriesOptions?.length > 0) {
            selectedReportMetadata?.filters?.states?.forEach((state) => {
                const foundState = usStatesAndTerritoriesOptions.find((opt) => opt.value === state);
                if (foundState) {
                    defaultStates.push(foundState);
                }
            });
            setSelectedStates(defaultStates);
        }
        if (childFilters?.length > 0) {
            selectedReportMetadata?.filters?.children?.forEach((child) => {
                const foundChild = childFilters.find((ch) => ch.value === child);
                if (foundChild) {
                    defaultChildren.push(foundChild);
                }
            });
            setSelectedChildren(defaultChildren);
        }
    };

    const handleChangeSelectedDateRange = () => {
        patchReport(null, null, null, null, null);
    };

    const handleChangeSelectedCategory = (category) => {
        setSelectedCategory(category);
        patchReport(category, null, null, null, null);
    };

    const handleChangeSelectedOperator = async (operator) => {
        setSelectedOperator(operator);
        const remainingParents = operator?.map(o => o.value) ?? [];
        if (remainingParents.length === 0) {
            setChildFilters([]);
            setSelectedChildren(null);
            patchReport(null, operator, null, null, null, null, null, []);
            return;
        }
        const options = await fetchChildrenForParents(operator);
        const validChildren =
            selectedChildren?.filter(child =>
            options.some(opt => opt.value === child.value)
            ) ?? [];
        setSelectedChildren(validChildren.length ? validChildren : null);
        patchReport(null, operator, null, null, null, null, null, validChildren);
    };

    const handleChangeSelectedChildren = (children) => {
        setSelectedChildren(children);
        patchReport(null, null, null, null, null, null, null, children);
    };

    const handleChangeSelectedProduct = (product) => {
        setSelectedProduct(product);
        patchReport(null, null, product, null, null);
    }

    const handleChangeSelectedSegment = (segment) => {
        setSelectedSegment(segment);
        patchReport(null, null, null, segment, null);
    }

    const handleChangeSelectedStates = (state) => {
        setSelectedStates(state);
        patchReport(null, null, null, null, state);
    }

    const handleClickBack = () => {
        navigate('/' + currentParentPage);
    };

    const handleEditNameClick = () => {
        setEditedName(selectedReportMetadata?.name || '');
        setIsEditingName(true);
    };

    const handleEditNameChange = (e) => {
        setEditedName(e.target.value);
    };

    const handleEditNameBlur = () => {
        saveEditedName();
    };

    const handleEditNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            saveEditedName();
        } else if (e.key === 'Escape') {
            setIsEditingName(false);
        }
    };

    const saveEditedName = () => {
        if (!editedName.trim() || editedName === selectedReportMetadata?.name) {
            setIsEditingName(false);
            return;
        }
        updateReportNameMutation.mutate({
            id: selectedReportMetadata?.id,
            name: editedName
        });
    };

    useEffect(() => {
        if (selectedReportMetadata) {
            handleReportChange(selectedReportMetadata);
            setDefaultFilters();
        }
        // eslint-disable-next-line
    }, [selectedReportMetadata]);

    const categoriesFilters = useMemo(() => {
        return reportFilters?.categories?.sort()?.map((filter) => ({value: filter, label: filter})) ?? [];
    }, [reportFilters]);

    const operatorsFilters = useMemo(() => {
        return reportFilters?.groupNames?.sort()?.map((filter) => ({value: filter, label: filter})) ?? [];
    }, [reportFilters]);

    const productFilters = useMemo(() => {
        return reportFilters?.products
            ?.sort((a,b) => (a.productName > b.productName) ? 1 : ((b.productName > a.productName) ? -1 : 0))
            ?.map((filter) => ({value: filter.productId, label: formatters().toTitleCase(filter.productName)})) ?? [];
    }, [reportFilters]);

    const segmentsFilters = useMemo(() => {
        return reportFilters?.segments?.sort()?.map((filter) => ({
            value: filter,
            label: formatters().toTitleCase(filter.replaceAll('_', ' '))
        })) ?? [];
    }, [reportFilters]);

    useEffect(() => {
        setDefaultFilters();
        // eslint-disable-next-line
    }, [categoriesFilters]);

    useEffect(() => {
        if (isEditingName && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditingName]);

    useEffect(() => {
        if (reportsListFetched) {
            setSelectedReportMetadata(getSelectedReportMetadata(reportId, reportsList));
            setReportSelectValue(reportSelectOptions?.find((option) => option.value === +reportId));
        }
    // eslint-disable-next-line
    }, [reportId, reportsListFetched]);

    useEffect(() => {
        if (selectedReportContents?.data?.length > 0) {
            setColumnKeys(Object.keys(selectedReportContents?.data?.[0]));
        }
    }, [selectedReportContents]);

    return (
        <Row>
            <Col>
                <PageHeader title='Reports' subtitle={selectedReportMetadata?.name} icon={<BsClipboardDataFill fill='url(#icon-gradient)'/>}/>
                <Row style={{marginTop: '16px'}}>
                    <Col className='main-header text-size-14 w-fc' style={{fontWeight: '700', letterSpacing:'.0025em', lineHeight: '16px', cursor: 'pointer'}} onClick={() => handleClickBack()}>
                        <FaArrowLeft color={Theme().colors.accent} size={'14'} style={{position: 'relative', top: '-1px'}}/><span style={{color: Theme().colors.accent}}> Back</span>
                    </Col>
                </Row>
                <Row className='label-text text-size-14 d-flex align-items-center mx-auto pb-2'>
                    <Col className='ps-0 col-3'>
                        <Label className='label-text-header mt-2 mb-1'>Now Viewing</Label>
                        <Select placeholder='Select Report' options={reportSelectOptions} onChange={(report) => handleSelectChange(report)} value={reportSelectValue} styles={selectStyles} />
                    </Col>
                    <Col className='ps-1 col-3 mt-auto'>
                        <ManufacturerSelect align="start" />
                    </Col>
                    <Col></Col>
                    <Col className='w-fc'>
                        <Button
                        outline
                        className='py-2 button-label'
                        size='sm'
                        style={{width: '70px', top: '16px', position: 'relative'}}
                        onClick={() => handleShareClick()}
                        color={Theme().colors.accentName}>Share
                        </Button>
                    </Col>
                    <Col className='w-fc'>
                        <Button
                            outline
                            className='py-2 button-label'
                            size='sm'
                            style={{width: '70px', top: '16px', position: 'relative'}}
                            onClick={() => exportReport(selectedReportMetadata?.id, selectedReportMetadata?.name, mfrId)}
                            disabled={isExporting}
                            color={Theme().colors.accentName}
                        >
                            Export
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card className='p-3' onMouseEnter={() => setIsReportHovered(true)} onMouseLeave={() => setIsReportHovered(false)}>
                            <Row style={{height: 'calc(100vh - 180px)'}}>
                                <Col style={{display: 'flex', flexDirection: 'column'}}>
                                    <Row>
                                        <Col className='w-fc pe-0'>
                                            {isStarred ? (
                                                <FaStar
                                                    size={'20px'}
                                                    color={Theme().colors.accent}
                                                    onClick={handleStarClick}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <FaRegStar
                                                    size={'20px'}
                                                    color={'black'}
                                                    onClick={handleStarClick}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            )}
                                        </Col>
                                        <Col className='w-fc pe-0'>
                                            <div
                                                className='main-header text-size-20'
                                                style={{
                                                    lineHeight: '23px',
                                                    fontWeight: '700',
                                                    letterSpacing: '.0025em',
                                                    cursor: isReportHovered && selectedReportMetadata?.ownerId ? 'pointer' : 'default',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: '28px'
                                                }}
                                                onClick={() => {
                                                    if (isReportHovered && selectedReportMetadata?.ownerId) handleEditNameClick();
                                                }}
                                                onMouseOver={() => setIsReportHovered(true)}
                                                onMouseOut={() => setIsReportHovered(false)}
                                            >
                                                {isEditingName ? (
                                                    <input
                                                        ref={inputRef}
                                                        type="text"
                                                        value={editedName}
                                                        onChange={handleEditNameChange}
                                                        onBlur={handleEditNameBlur}
                                                        onKeyDown={handleEditNameKeyDown}
                                                        style={{
                                                            fontSize: '20px',
                                                            fontWeight: '700',
                                                            width: '100%',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            padding: '2px 6px'
                                                        }}
                                                        maxLength={100}
                                                    />
                                                ) : (
                                                    <span
                                                        title={selectedReportMetadata?.ownerId ? "Click to edit name" : undefined}
                                                        style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                                    >
                                                        {selectedReportMetadata?.name}
                                                        {isReportHovered && selectedReportMetadata?.ownerId && (
                                                            <FaRegEdit
                                                                style={{
                                                                    marginLeft: 8,
                                                                    color: Theme().colors.accent,
                                                                    fontSize: 18,
                                                                    verticalAlign: 'middle'
                                                                }}
                                                                title="Edit report name"
                                                            />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        {!selectedReportMetadata?.ownerId &&
                                        <Col className='w-fc px-1'>
                                            <Tooltip title={"Any changes to the filters on this report will not be saved. Please make a copy of the report to make permanent changes."} placement="top">
                                                <div>
                                                    <IoMdInformationCircle style={{fontSize: "18px", color: Theme().colors.accent, position: 'relative', bottom: '2px'}} />
                                                </div>
                                            </Tooltip>
                                        </Col>
                                        }
                                        <Col></Col>
                                        {!selectedReportMetadata?.ownerId &&
                                        <Col className='w-fc'>
                                            <Button
                                                block
                                                className='button-label'
                                                size='sm'
                                                style={{width: '70px'}}
                                                onClick={() => handleCopyReport()}
                                                color={Theme().colors.accentName}>Save As Copy
                                            </Button>
                                        </Col>
                                        }
                                        {(isReportHovered && selectedReportMetadata?.ownerId) &&
                                        <Col className='w-fc'>
                                            <Button
                                                block
                                                className='button-label'
                                                style={{height: '24px', width: '24px'}}
                                                title='Delete Report'
                                                size='sm'
                                                color='redLight'
                                                onClick={() => {handleDeleteReport()}}>
                                                <FaTrash color='white' style={{top: '-5px', position: 'relative'}} />
                                            </Button>
                                        </Col>
                                        }
                                    </Row>
                                    <Card className='p-2' style={{background:'url("./FiltersBackground.png")', backgroundRepeat: 'no-repeat', backgroundSize: 'cover'}}>
                                        <Row className='mb-2'>
                                            <div className="main-header text-size-20" style={{color: '#00A6CC'}}><FaFilter fontSize={'15px'} style={{marginBottom: '5px'}}/> Filters</div>
                                        </Row>
                                        {(filterLoading && selectedReportMetadata?.ownerId) &&
                                        <Row>
                                            <Col>
                                                <div className='main-header' style={{textAlign: 'center', height: '38px'}}>Filters are loading...</div>
                                            </Col>
                                        </Row>
                                        }
                                        {!selectedReportMetadata?.ownerId &&
                                        <Row>
                                            <Col>
                                                <div className='main-header' style={{textAlign: 'center', height: '38px'}}>Please copy report first to use filters</div>
                                            </Col>
                                        </Row>
                                        }
                                        {(!filterLoading && selectedReportMetadata?.ownerId) &&
                                        <Row style={{'--bs-gutter-y': '8px'}}>
                                    <Col style={{minWidth: '200px'}}>
                                        <DateRangePicker
                                        id='dashboardDateSearch'
                                        defaultStartDate={moment(startDate).format('MM/DD/YYYY')}
                                        defaultEndDate={moment(endDate).format('MM/DD/YYYY')}
                                        minDate={moment(startDate).format('MM/DD/YYYY')}
                                        maxDate={moment().format('MM/DD/YYYY')}
                                        callback={(start, end) => {setSelectedDateRange({startDate: start, endDate: end}); handleChangeSelectedDateRange();}}
                                        ranges={{
                                            'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                                            'Last 2 Months': [moment().subtract(2, 'month'), moment()],
                                            'Last Year': [moment().subtract(1, 'year'), moment()]
                                            }
                                        }
                                        />
                                    </Col>
                                    <Col style={{minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select Category'
                                            isMulti={true}
                                            isLoading={filterLoading}
                                            options={categoriesFilters}
                                            value={selectedCategory}
                                            onChange={(category) => handleChangeSelectedCategory(category)}
                                        />
                                    </Col>
                                    <Col style={{minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select Parent Group'
                                            isMulti={true}
                                            isLoading={filterLoading}
                                            options={operatorsFilters}
                                            value={selectedOperator}
                                            onChange={(operator) => handleChangeSelectedOperator(operator)}
                                        />
                                    </Col>
                                    <Col style={{minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select Child Location'
                                            isMulti={true}
                                            isLoading={childLoading}
                                            isDisabled={!selectedOperator || selectedOperator.length === 0}
                                            options={childFilters}
                                            value={selectedChildren}
                                            onChange={(children) => handleChangeSelectedChildren(children)}
                                        />
                                    </Col>
                                    <Col style={{textTransform: 'capitalize', minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select Product'
                                            isMulti={true}
                                            isLoading={filterLoading}
                                            options={productFilters}
                                            value={selectedProduct}
                                            onChange={(product) => handleChangeSelectedProduct(product)}
                                        />
                                    </Col>
                                    <Col style={{textTransform: 'capitalize', minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select Segment'
                                            isMulti={true}
                                            isLoading={filterLoading}
                                            options={segmentsFilters}
                                            value={selectedSegment}
                                            onChange={(segment) => handleChangeSelectedSegment(segment)}
                                        />
                                    </Col>
                                    <Col style={{minWidth: '200px'}}>
                                        <SelectAllSearch
                                            placeholder='Select State'
                                            isMulti={true}
                                            isLoading={filterLoading}
                                            options={usStatesAndTerritoriesOptions}
                                            value={selectedStates}
                                            onChange={(state) => handleChangeSelectedStates(state)}
                                        />
                                    </Col>
                                    <Col className='w-fc'>
                                        <Button
                                            block
                                            className='py-2 button-label'
                                            size='sm'
                                            style={{width: '70px'}}
                                            onClick={() => handleCopyReport()}
                                            color={Theme().colors.accentName}>Save As Copy
                                        </Button>
                                    </Col>
                                        </Row>
                                        }
                                    </Card>
                                    {selectedReportContents?.data?.length > 0 && !reportLoading &&
                                    <Row style={{flex: '1'}}>
                                        <Col style={{height: '100%'}}>
                                            <AutoSizer>
                                                {({ width, height }) => (
                                                    <Table className="table reportsTable border-grayLight"
                                                    width={width}
                                                    height={height}
                                                    headerHeight={34.5}
                                                    rowHeight={38}
                                                    rowCount={selectedReportContents?.data?.length}
                                                    rowGetter={({ index }) => selectedReportContents?.data?.[index]}
                                                    ref={tableRef}
                                                    >
                                                        {widgetColumns}
                                                    </Table>
                                                )}
                                            </AutoSizer>
                                        </Col>
                                    </Row>
                                    }
                                    {reportLoading &&
                                    <Row style={{height: 'calc(100% - 180px)'}}>
                                        <Col className='d-flex justify-content-center align-items-center h-100'>
                                            <div className='spinner-border text-primary' role='status'>
                                                <span className='visually-hidden'>Loading...</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    }
                                    {selectedReportContents?.data?.length <= 0 && !reportLoading &&
                                    <Row style={{height: 'calc(100% - 180px)'}}>
                                        <Col>
                                            <EmptyDataMessage />
                                        </Col>
                                    </Row>
                                    }
                                    <Row className='mx-1' style={{borderTop: '1px solid lightgray', paddingTop: '12px'}}>
                                        <Col className='w-fc mx-auto'>
                                            <Pagination withEdges={true} total={selectedReportContents?.totalPages} onChange={(page) => setSelectedReportPage(page)} color={Theme().colors.secondary}/>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
}

export default ReportWidget;
